import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { CopyrightRecord } from '../../types/blockchain';
import blockchainService from '../../services/blockchainService';
import { DocumentCheckIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const CopyrightManager: React.FC = () => {
  const { wallet } = useWeb3();
  const [copyrightRecords, setCopyrightRecords] = useState<CopyrightRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedAudioFile, setSelectedAudioFile] = useState('');

  useEffect(() => {
    if (wallet?.address) {
      loadCopyrightRecords();
    }
  }, [wallet]);

  const loadCopyrightRecords = async () => {
    if (!wallet?.address) return;
    
    setLoading(true);
    try {
      const { UnifiedMockData } = await import('../../data/mock');
      const mockData = new UnifiedMockData();
      const records = mockData.getCopyrightRecords(wallet.address);
      setCopyrightRecords(records);
    } catch (error) {
      console.error('Failed to load copyright records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterCopyright = async (workId: string, title: string, audioFile: File) => {
    if (!wallet?.address) return;
    
    setRegistering(true);
    try {
      const audioHash = await blockchainService.calculateFileHash(audioFile);
      const record = await blockchainService.registerCopyright(workId, audioHash, {
        title,
        creator: 'Current User',
        collaborators: []
      });
      
      setCopyrightRecords(prev => [record, ...prev]);
    } catch (error) {
      console.error('Failed to register copyright:', error);
    } finally {
      setRegistering(false);
    }
  };

  const getStatusIcon = (status: CopyrightRecord['status']) => {
    switch (status) {
      case 'confirmed':
        return <DocumentCheckIcon className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 协作项目数据
  const collaborationProjects = [
    { id: 'collab_1', name: '《星空下的旋律》', audioFile: 'work_1' },
    { id: 'collab_2', name: '《夏日回忆》', audioFile: 'work_2' },
    { id: 'collab_3', name: '《城市夜景》', audioFile: 'work_3' },
    { id: 'collab_4', name: '《梦想起航》', audioFile: 'work_4' },
    { id: 'collab_5', name: '《温柔时光》', audioFile: 'work_5' }
  ];

  // 音频文件选项
  const audioFileOptions = [
    { value: 'work_1', label: '《星空下的旋律》- 电子音乐 (3:45)' },
    { value: 'work_2', label: '《夏日回忆》- 流行音乐 (4:12)' },
    { value: 'work_3', label: '《城市夜景》- 爵士音乐 (5:23)' },
    { value: 'work_4', label: '《梦想起航》- 摇滚音乐 (3:58)' },
    { value: 'work_5', label: '《温柔时光》- 民谣音乐 (4:35)' }
  ];

  // 处理协作项目选择
  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
    const project = collaborationProjects.find(p => p.id === projectId);
    if (project) {
      setSelectedAudioFile(project.audioFile);
    }
  };

  if (!wallet?.isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">请先连接钱包以查看版权记录</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">版权管理</h1>
        <p className="text-gray-600">管理您的音乐作品版权记录</p>
        
        {/* 区块链核心价值展示 */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">🔒 协作版权保护</h3>
            <p className="text-sm text-blue-700">区块链确保协作作品的版权归属和分成比例不可篡改，每位协作者的贡献都有明确记录。</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">💰 透明收益分配</h3>
            <p className="text-sm text-green-700">智能合约自动执行收益分配，确保每位协作者按约定比例获得收益，无需人工干预。</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-800 mb-2">📈 NFT投资价值</h3>
            <p className="text-sm text-purple-700">作品NFT化后具有投资价值，早期支持者可享受作品增值收益，形成良性投资生态。</p>
          </div>
        </div>
      </div>

      {/* 注册新版权 */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">从协作项目注册版权</h2>
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm">
            💡 版权注册来源于已完成的协作项目。请从您参与的协作项目中选择要注册版权的作品。
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">选择协作项目</label>
            <select 
              value={selectedProject}
              onChange={(e) => handleProjectChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">请选择要注册版权的协作项目</option>
              <option value="collab_1">《星空下的旋律》- 与张音乐、李制作协作</option>
              <option value="collab_2">《夏日回忆》- 与王歌手协作</option>
              <option value="collab_3">《城市夜景》- 与赵编曲、钱混音协作</option>
              <option value="collab_4">《梦想起航》- 与李吉他、陈鼓手协作</option>
              <option value="collab_5">《温柔时光》- 与刘作词协作</option>
            </select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">作品标题</label>
              <input
                type="text"
                placeholder="将从协作项目自动填充"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">协作完成时间</label>
              <input
                type="text"
                placeholder="将从协作项目自动填充"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                disabled
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">协作者信息</label>
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <p className="text-sm text-gray-600">协作者信息将从选定的协作项目中自动获取，包括贡献比例和收益分成。</p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">最终音频文件</label>
            <select 
              value={selectedAudioFile}
              onChange={(e) => setSelectedAudioFile(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">请选择创作平台中的作品</option>
              {audioFileOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {selectedProject ? '已根据协作项目自动选择对应的音频文件' : '从您在创作者平台中的作品库选择要注册版权的音频文件'}
            </p>
          </div>
          
          <button
            disabled={registering}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <DocumentCheckIcon className="w-5 h-5" />
            {registering ? '正在提交版权注册...' : '提交版权注册申请'}
          </button>
        </div>
      </div>

      {/* 版权记录列表 */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">我的版权记录</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">加载中...</p>
          </div>
        ) : copyrightRecords.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            暂无版权记录
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {copyrightRecords.map((record) => (
              <div key={record.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(record.status)}
                      <h3 className="text-lg font-medium text-gray-900">
                        {record.workTitle}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        record.status === 'confirmed' 
                          ? 'bg-green-100 text-green-800'
                          : record.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {record.status === 'confirmed' ? '已确认' : 
                         record.status === 'pending' ? '待确认' : '失败'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">来源:</span>
                        <span className="ml-2 text-blue-600">协作项目完成</span>
                      </div>
                      <div>
                        <span className="font-medium">音频哈希:</span>
                        <span className="ml-2 font-mono">{record.audioHash.substring(0, 20)}...</span>
                      </div>
                      <div>
                        <span className="font-medium">区块高度:</span>
                        <span className="ml-2">{record.blockNumber.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="font-medium">注册时间:</span>
                        <span className="ml-2">{formatDate(record.registrationDate)}</span>
                      </div>
                      <div>
                        <span className="font-medium">交易哈希:</span>
                        <span className="ml-2 font-mono">{record.transactionHash.substring(0, 20)}...</span>
                      </div>
                      <div>
                        <span className="font-medium">协作状态:</span>
                        <span className="ml-2 text-green-600">已完成并确认</span>
                      </div>
                    </div>

                    {record.collaborators && record.collaborators.length > 0 && (
                      <div className="mt-4">
                        <div className="font-medium text-sm text-gray-600 mb-2">协作者:</div>
                        <div className="flex flex-wrap gap-2">
                          {record.collaborators.map((collab, index) => (
                            <span
                              key={index}
                              className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                            >
                              {collab.name} ({collab.sharePercentage}%)
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CopyrightManager;
